import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/toast-context";
import { ManagerService } from "../services/manager-service";
import { ManagerResponseDto } from "../services/dto/response/manager-response-dto";
import { AddManager } from "../components/managers/add-manager";
import { ManagerItem } from "../components/managers/manager-item";
import { ManagerFilter } from "../components/managers/manager-filter";
import { Loading } from "../components/Loading";
import { ManagerPaycloud } from "../types";
import { useLocationPreload } from "../hooks/useLocationPreload";

export function Managers() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [managers, setManagers] = useState<ManagerPaycloud[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const managerService = useMemo(() => new ManagerService(), []);

  // Precargar datos de ubicación cuando se entra a la pantalla de managers
  useLocationPreload();

  const fetchManagers = async (active?: boolean) => {
    setIsLoading(true);
    try {
      await managerService.getAllManagers(
        { active },
        (data) => {
          setManagers(data);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } catch (error) {
      showToast("Error fetching managers", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchManagers = async () => {
      setIsLoading(true);
      try {
        await managerService.getAllManagers(
          statusFilter === "all" ? undefined : { active: statusFilter === "active" },
          (data) => {
            if (!signal.aborted) {
              setManagers(data);
            }
          },
          (errorMessage) => {
            if (!signal.aborted) {
              showToast(errorMessage, "error");
            }
          }
        );
      } catch (error) {
        if (!signal.aborted) {
          showToast("Error fetching managers", "error");
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchManagers();
    return () => {
      controller.abort();
    };
  }, [managerService, showToast, statusFilter]);

  const handleAddManager = async (newManager: ManagerResponseDto) => {
    try {
      await managerService.getAllManagers(
        statusFilter === "all" ? undefined : { active: statusFilter === "active" },
        (data) => {
          setManagers(data);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } catch (error) {
      showToast("Error fetching managers", "error");
    }
  };

  const handleDeleteManager = async (managerId: number) => {
    setManagers((prev) => {
      const index = prev.findIndex((m) => m.userId === managerId);
      if (index === -1) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], active: false };
      return updated;
    });
  };
  

  // const handleEditManager = async (managerId: number, updatedManager: ManagerRequestDto) => {
  //   setManagers((prev) => prev.map((manager) => manager.userId === managerId ? updatedManager : manager));
  // };

  return (
    <main className="h-full flex flex-col rounded-3xl bg-[#a0abba] p-6 overflow-hidden">
      <div className="mb-4 flex justify-between items-center">
        <ManagerFilter 
          statusFilter={statusFilter} 
          onStatusFilterChange={setStatusFilter} 
        />
        <AddManager onAddManager={handleAddManager} fetchManagers={() => fetchManagers()} />
      </div>

      {/* Contenido principal */}
      <div className="flex flex-col flex-1 gap-2 h-full overflow-y-auto custom-scrollbar rounded-lg p-4">
        {isLoading && <Loading />}
        {!isLoading && managers.length === 0 && (
          <div className="w-full h-full flex justify-center items-center text-white text-2xl">
            <p>{t("managers.no-managers")}</p>
          </div>
        )}
        {!isLoading &&
          managers.length > 0 &&
          managers.map((manager) => (
            <ManagerItem
              key={manager.userId} 
              manager={manager}
              onDeleteManager={handleDeleteManager}
              fetchManagers={fetchManagers}
            />
          ))}
      </div>
    </main>
  );
} 